export interface StudentSsoSiteInfo {
  clever_sso_enabled?: boolean | null;
  /** Not sent by Core today — see resolveStudentSsoProvider. */
  classlink_sso_enabled?: boolean | null;
}

/**
 * Resolve the SSO provider STUDENTS sign in with on this site, or null.
 *
 * Clever/ClassLink SSO is a SITE-level student LOGIN method
 * (`siteInfo.clever_sso_enabled` / `classlink_sso_enabled`). When enabled, a
 * student's "access code" is meaningless — they sign in via SSO, not a code —
 * so the Student Details "Manage Access" student row shows the provider with no
 * access code and no copy link (ENG-9241).
 *
 * Clever keys on the LOGIN method (the SSO flag), NOT the rostering source
 * (`provider_name`): a site ROSTERED via Clever whose students still log in with
 * a password or QR badge keeps its access code. ClassLink has no flag at all, so
 * it needs the student — see the comment in the body.
 *
 * Parents are unaffected — they register with an access code even on SSO sites
 * (parents don't use Clever/ClassLink; only students and staff do).
 *
 * @param student - The student whose row is being rendered, when there is one.
 *   Callers asking about the SITE (the Launch checklist's invite step, access-
 *   sheet visibility) omit it and keep flag-only behavior.
 * @param rosterProvider - ENG-9603/ENG-9614: the student's OWN roster provider
 *   from portal-stats (`roster_provider`), derived server-side from
 *   partner_ids — the same table SSO login resolves identity through. When
 *   present it is authoritative:
 *   - 'manual'    → never SSO, whatever the site flags say. A manually created
 *                   or CSV-imported student at a Clever/ClassLink school has no
 *                   provider identity to sign in with; hiding their access code
 *                   locks them out entirely.
 *   - 'clever'    → SSO only if the site's login method is Clever SSO
 *                   (`clever_sso_enabled`) — rostering alone never hides a code
 *                   (ENG-9241).
 *   - 'classlink' → ClassLink (replaces the provider_name inference below).
 *   Omitted/undefined (older cached responses, flag-only callers) keeps the
 *   pre-ENG-9603 behavior.
 */
export function resolveStudentSsoProvider(
  siteInfo: StudentSsoSiteInfo | null | undefined,
  student?: SiteProviderInfo | null,
  rosterProvider?: 'manual' | 'clever' | 'classlink',
): 'Clever' | 'ClassLink' | null {
  if (rosterProvider === 'manual') return null;
  if (rosterProvider === 'clever') {
    return siteInfo?.clever_sso_enabled ? 'Clever' : null;
  }
  if (rosterProvider === 'classlink') return 'ClassLink';

  if (siteInfo?.clever_sso_enabled) return 'Clever';
  if (siteInfo?.classlink_sso_enabled) return 'ClassLink';
  // ponytail: Core has no `classlink_sso_enabled`. Verified against live gamma
  // school 5644, whose site payload carries `clever_sso_enabled: false` and
  // `provider_name: 'classlink'` but no ClassLink flag at all — so the check
  // above could never fire and every ClassLink student's row read "No email"
  // (ENG-9783). ClassLink SSO is therefore inferred from `provider_name`, the
  // same field the auth store reads to pick the login method — but from the
  // STUDENT's, which is what the legacy card one section above already trusts
  // (isSyncedStudent) and what ENG-9614 wants SSO keyed on. Taking it off the
  // SITE instead would also silently reframe the Launch checklist's invite step
  // and access-sheet visibility, which read this helper with no student.
  // Clever deliberately keeps its explicit flag: a Clever-ROSTERED site whose
  // students log in with a password still owns its access codes (ENG-9241), and
  // `clever_sso_enabled` is what tells the two apart. Drop this fallback if Core
  // ever ships a real `classlink_sso_enabled`.
  if (resolveSiteSsoProvider(student) === 'ClassLink') return 'ClassLink';
  return null;
}

export interface SiteProviderInfo {
  /** The site's SSO/rostering provider, e.g. 'clever' | 'classlink'. */
  provider_name?: string | null;
}

/**
 * The SITE's SSO/rostering provider (Clever/ClassLink), or null.
 *
 * Keys on `siteInfo.provider_name` — the same field the auth store reads to pick
 * the login method (Clever/ClassLink SSO). This is the right signal for "is this
 * a Clever/ClassLink site", and is DISTINCT from `resolveStudentSsoProvider`,
 * which keys on the per-student login flag (`*_sso_enabled`) for access-code
 * display. Used to decide whether staff sign in via SSO (no invites to send).
 */
export function resolveSiteSsoProvider(
  siteInfo: SiteProviderInfo | null | undefined,
): 'Clever' | 'ClassLink' | null {
  const provider = siteInfo?.provider_name?.toLowerCase();
  if (provider === 'clever') return 'Clever';
  if (provider === 'classlink') return 'ClassLink';
  return null;
}
